import { ApiError, type Api } from "../api/client.js";
import type { PayeeAddStep, PayeeView, SupportContact } from "../api/types.js";
import type { AimHandlers } from "../ui/aim.js";
import { PayeesView } from "../ui/payees.js";
import { openSheet, passkeySheet, supportSheet, type SheetHandle } from "../ui/sheet.js";
import type { Tab } from "../ui/tabs.js";
import { AddFlow, type SignerLike } from "./add-flow.js";
import { addRequest, foundState, lookupFailed, nicknameProblem, proxyOf, proxyLabel, ADD_COPY, type ProxyInput } from "./add-payee-model.js";
import { CardSigner } from "./card-signer.js";
import { whenFocused } from "./passkey.js";
import { initialOf, payeeDetail, payeesModel, type PayeeDetail } from "./payees-model.js";
import { proxyTail } from "../lib/format.js";

/**
 * 17 Payees' controller (DCTA-111 run 2): `GET /v1/payees` for the list, `GET /v1/payees/{id}` for a
 * payee's page (read fresh — the list may be minutes old and a wait may have ended). A page that
 * cannot be read falls back to the list's own copy of the payee, so a tap never lands on nothing;
 * nothing here writes. The page's history entry is the caller's (`onDetail`).
 */
export interface PayeesDeps {
  readonly api: Api;
  /** The bank's clock, in ms (lib/bank-clock.ts): every wait is measured against it. */
  readonly now: () => number;
  readonly onBack: () => void;
  /** A payee's page opened (true) or closed itself (false): the caller keeps one history entry for it. */
  readonly onDetail: (open: boolean) => void;
  readonly onSignedOut: () => void;
  readonly onTab: (tab: Tab) => void;
  readonly assistant: AimHandlers;
  /** DCTA-120: the list's + → 17 at `add` (the caller keeps its history entry). */
  readonly onAdd?: (() => void) | undefined;
  /** The add screen's own back. */
  readonly onAddBack?: (() => void) | undefined;
  /** Talk to us rises in this column (`#app`). */
  readonly host?: HTMLElement | undefined;
  readonly reduced?: (() => boolean) | undefined;
  /** DCTA-124: whose passkey signs an add (this phone's note that it holds one is per customer). */
  readonly customerId?: string | undefined;
  /** A test's own signer; the app signs with CardSigner. */
  readonly signer?: ((bundleId: string) => SignerLike) | undefined;
  /** DCTA-199 ruling: the add's card, drawn — to be read aloud ("Check and sign."). */
  readonly onCard?: ((bundleId: string) => void) | undefined;
}

export class PayeesScreen {
  readonly view: PayeesView;
  readonly #d: PayeesDeps;
  #payees: readonly PayeeView[] | undefined;
  #opening = false;
  /** One lookup at a time; a newer Continue supersedes an older answer (`#asked`). */
  #asked = 0;
  #support: SupportContact | undefined;
  #sheet: SheetHandle | undefined;
  /** DCTA-124: the number the bank last named (the add is asked for THIS one), and where 17 at add came from. */
  #found: ProxyInput | undefined;
  #from: "list" | "chat" = "list";
  readonly #flow: AddFlow;

  constructor(deps: PayeesDeps) {
    this.#d = deps;
    this.view = new PayeesView({
      onBack: deps.onBack,
      onOpen: (id) => void this.#open(id),
      onDetailBack: () => {
        this.closeDetail();
        deps.onDetail(false);
      },
      onRetry: () => void this.refresh(),
      onAdd: () => deps.onAdd?.(),
      onAddBack: () => deps.onAddBack?.(),
      onLookup: (typed) => void this.#lookup(typed),
      onTalk: () => void this.#talk(),
      onCheck: (nickname) => this.#check(nickname),
      onSignBack: () => this.#signBack(),
      onTab: deps.onTab,
      assistant: deps.assistant,
    });
    this.#flow = new AddFlow({
      api: deps.api,
      view: this.view.signView,
      signer:
        deps.signer ??
        ((bundleId) =>
          new CardSigner(
            {
              api: deps.api,
              customerId: deps.customerId ?? "",
              credentials: navigator.credentials,
              storage: localStorage,
              focus: () => whenFocused(document, window, 1000),
              now: () => Date.now(),
            },
            bundleId,
          )),
      onTalk: () => void this.#talk(),
      onNotNow: () => deps.onAddBack?.(),
      onAdded: (payee, done, signed) => this.#added(payee, done, signed),
      onSignedOut: deps.onSignedOut,
      onUnsupported: () => this.#unsupported(),
      onCard: deps.onCard,
    });
  }

  /**
   * The system's Back while 17 at add is open (DCTA-124): a passkey sheet up → refused (nothing moves);
   * Check and sign open → back to the name (the card cancelled at the bank). `false`: not handled here —
   * the add screen itself goes.
   */
  stepBack(): boolean {
    if (this.#flow.busy) return true;
    if (this.view.signView.open) {
      this.#signBack();
      return true;
    }
    return false;
  }

  #signBack(): void {
    if (this.#flow.busy) return;
    this.#flow.back();
    this.view.focusCheck();
  }

  /** Check and sign (13a): the customer's name for them checked at the field, then the bank builds the card. */
  #check(nickname: string): void {
    const found = this.#found;
    if (found === undefined) return;
    const problem = nicknameProblem(nickname);
    if (problem !== undefined) {
      this.view.nickError(problem);
      return;
    }
    this.#flow.start(addRequest(found, nickname));
  }

  /**
   * 13f: the bank added them — their page as the bank reads it now ("Payments can go out from …"), with
   * "{Name} added"; the list under it is read again. If the read failed, the page says what was signed.
   */
  #added(payee: PayeeView | undefined, done: string, signed: PayeeAddStep["payee"]): void {
    const name = signed.nickname ?? signed.registered_name;
    const d: PayeeDetail =
      payee !== undefined
        ? payeeDetail(payee, this.#d.now())
        : {
            initial: initialOf(name),
            name,
            registered: signed.registered_name,
            kv: [
              ["Name at the bank", signed.registered_name],
              ["Paid by", `${proxyLabel(signed.proxy_type)} ${proxyTail(signed.masked_proxy)}`],
            ],
          };
    this.view.showAdded(d, done, this.#from === "chat" ? "Back to Assistant" : "Back to Payees");
    void this.refresh();
  }

  /** No passkeys on this phone: the passkey sheet's "not available here" (as the chat says it). */
  #unsupported(): void {
    const host = this.#d.host;
    if (host === undefined || this.#sheet !== undefined) return;
    this.#sheet = openSheet(host, passkeySheet("unsupported", { primary: { label: "OK", onSelect: () => void this.#closeSheet() } }).sheet, {
      behind: this.view.parts,
      reduced: this.#d.reduced?.() ?? false,
      animate: false,
      onDismiss: () => void this.#closeSheet(),
    });
  }

  /** 17 at `add` (DCTA-120): over the list, or at once over the chat (`from: "chat"`). */
  openAdd(from: "list" | "chat"): void {
    this.#asked += 1;
    this.#from = from;
    this.#found = undefined;
    this.view.pushAdd(from === "chat" ? "Back to Assistant" : "Back to Payees", from === "chat");
  }

  closeAdd(): void {
    this.#asked += 1;
    this.#found = undefined;
    // A card still waiting on Check and sign is cancelled at the bank as the screen goes.
    this.#flow.close();
    void this.#closeSheet();
    this.view.popAdd();
  }

  /**
   * Continue: the typed words, read as ONE PayNow proxy (`proxyOf` — the customer's typing, never the
   * bank's words), looked up in the directory (`POST /v1/paynow/lookup`). Nothing is sent for words
   * that are not a proxy; nothing is saved by a lookup. A newer Continue (or leaving) drops an older answer.
   */
  async #lookup(typed: string): Promise<void> {
    const proxy = proxyOf(typed);
    if (proxy === undefined) {
      this.view.addState({ kind: "error", text: ADD_COPY.invalid, talk: false });
      return;
    }
    const asked = ++this.#asked;
    this.#found = undefined;
    this.view.addState({ kind: "checking" });
    try {
      const found = await this.#d.api.paynowLookup(proxy.proxy_type, proxy.proxy);
      if (asked !== this.#asked) return;
      this.#found = proxy;
      this.view.addState(foundState(found));
    } catch (error) {
      if (error instanceof ApiError && error.reasonCode === "AUTH_REQUIRED") {
        this.#d.onSignedOut();
        return;
      }
      if (asked === this.#asked) this.view.addState(lookupFailed(error));
    }
  }

  /** Talk to us: the bank's number and hours (`GET /v1/support`, the operator's config), on a sheet. */
  async #talk(): Promise<void> {
    const host = this.#d.host;
    if (host === undefined || this.#sheet !== undefined) return;
    try {
      this.#support ??= await this.#d.api.support();
    } catch {
      this.view.addState({ kind: "error", text: ADD_COPY.unreachable, talk: false });
      return;
    }
    this.#sheet = openSheet(host, supportSheet(this.#support, () => void this.#closeSheet()), {
      behind: this.view.parts,
      reduced: this.#d.reduced?.() ?? false,
      animate: false,
      onDismiss: () => void this.#closeSheet(),
    });
  }

  async #closeSheet(): Promise<void> {
    const sheet = this.#sheet;
    this.#sheet = undefined;
    await sheet?.close();
  }

  async refresh(): Promise<void> {
    if (this.#payees === undefined) this.view.loading();
    try {
      const r = await this.#d.api.payees();
      this.#payees = r.payees;
      this.view.show(payeesModel(r.payees, this.#d.now()));
    } catch (error) {
      if (error instanceof ApiError && error.reasonCode === "AUTH_REQUIRED") {
        this.#d.onSignedOut();
        return;
      }
      this.#payees = undefined;
      this.view.unreachable();
    }
  }

  /** The page goes (its own back, the system's Back). */
  closeDetail(): void {
    this.view.popDetail();
  }

  async #open(id: string): Promise<void> {
    if (this.#opening || this.view.detailOpen) return;
    this.#opening = true;
    try {
      let payee: PayeeView | undefined;
      try {
        payee = await this.#d.api.payee(id);
      } catch (error) {
        if (error instanceof ApiError && error.reasonCode === "AUTH_REQUIRED") {
          this.#d.onSignedOut();
          return;
        }
        payee = this.#payees?.find((p) => p.payee_id === id);
      }
      if (payee === undefined) return;
      this.view.pushDetail(payeeDetail(payee, this.#d.now()), id);
      this.#d.onDetail(true);
    } finally {
      this.#opening = false;
    }
  }
}
